
import { Component, Vue } from 'vue-property-decorator'
import {
  listSkus,
  createSku,
  updateSku,
  deleteSku,
  addFeatureToSku,
  removeFeatureFromSku,
  getAvailableFeatures,
  Sku,
  Feature
} from '@/api/skus'
import Pagination from '@/components/Pagination/index.vue'

@Component({
  name: 'SkuManagement',
  components: {
    Pagination
  }
})
export default class extends Vue {
  private skus: Sku[] = []
  private total = 0
  private listLoading = false
  private listQuery = {
    page: 1,
    limit: 20,
    productType: undefined,
    isActive: undefined,
    search: undefined
  }

  private dialogFormVisible = false
  private dialogStatus = 'create'
  private formData: any = {
    skuCode: '',
    name: '',
    description: '',
    productType: 'V2',
    isActive: true
  }

  private rules = {
    skuCode: [{ required: true, message: 'SKU Code is required', trigger: 'blur' }],
    name: [{ required: true, message: 'Name is required', trigger: 'blur' }],
    productType: [{ required: true, message: 'Product Type is required', trigger: 'change' }]
  }

  private featuresDialogVisible = false
  private selectedSku: Sku | null = null
  private availableFeatures: Feature[] = []
  private newFeatureForm = {
    featureId: null as number | null,
    durationType: 'permanent',
    durationValue: 1
  }

  created() {
    this.getList()
    this.loadAvailableFeatures()
  }

  private async getList() {
    this.listLoading = true
    try {
      const { data } = await listSkus(this.listQuery)
      this.skus = data.data.skus
      this.total = data.data.pagination.total
    } catch (error) {
      console.error('Failed to load SKUs:', error)
      this.$message.error('Failed to load SKUs')
    } finally {
      this.listLoading = false
    }
  }

  private async loadAvailableFeatures() {
    try {
      const { data } = await getAvailableFeatures()
      this.availableFeatures = data.data
    } catch (error) {
      console.error('Failed to load features:', error)
    }
  }

  private handleFilter() {
    this.listQuery.page = 1
    this.getList()
  }

  private resetForm() {
    this.formData = {
      skuCode: '',
      name: '',
      description: '',
      productType: 'V2',
      isActive: true
    }
  }

  private handleCreate() {
    this.resetForm()
    this.dialogStatus = 'create'
    this.dialogFormVisible = true
    this.$nextTick(() => {
      (this.$refs.dataForm as any).clearValidate()
    })
  }

  private handleEdit(row: Sku) {
    this.formData = {
      id: row.id,
      skuCode: row.skuCode,
      name: row.name,
      description: row.description,
      productType: row.productType,
      isActive: row.isActive
    }
    this.dialogStatus = 'edit'
    this.dialogFormVisible = true
    this.$nextTick(() => {
      (this.$refs.dataForm as any).clearValidate()
    })
  }

  private async createSkuHandler() {
    (this.$refs.dataForm as any).validate(async(valid: boolean) => {
      if (valid) {
        try {
          await createSku(this.formData)
          this.$message.success('SKU created successfully')
          this.dialogFormVisible = false
          this.getList()
        } catch (error: any) {
          console.error('Failed to create SKU:', error)
          this.$message.error(error.response?.data?.status?.message || 'Failed to create SKU')
        }
      }
    })
  }

  private async updateSkuHandler() {
    (this.$refs.dataForm as any).validate(async(valid: boolean) => {
      if (valid) {
        try {
          await updateSku(this.formData.id, this.formData)
          this.$message.success('SKU updated successfully')
          this.dialogFormVisible = false
          this.getList()
        } catch (error: any) {
          console.error('Failed to update SKU:', error)
          this.$message.error(error.response?.data?.status?.message || 'Failed to update SKU')
        }
      }
    })
  }

  private async handleDelete(row: Sku) {
    this.$confirm(`Delete SKU "${row.skuCode}"? This will also remove all feature assignments.`, 'Warning', {
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      type: 'warning'
    }).then(async() => {
      try {
        await deleteSku(row.id)
        this.$message.success('SKU deleted successfully')
        this.getList()
      } catch (error) {
        console.error('Failed to delete SKU:', error)
        this.$message.error('Failed to delete SKU')
      }
    })
  }

  private async handleManageFeatures(row: Sku) {
    this.selectedSku = row
    this.newFeatureForm = {
      featureId: null,
      durationType: 'permanent',
      durationValue: 1
    }
    this.featuresDialogVisible = true
  }

  private async handleAddFeature() {
    if (!this.selectedSku || !this.newFeatureForm.featureId) {
      this.$message.warning('Please select a feature')
      return
    }

    try {
      await addFeatureToSku(this.selectedSku.id, {
        featureId: this.newFeatureForm.featureId,
        durationType: this.newFeatureForm.durationType,
        durationValue: this.newFeatureForm.durationType === 'permanent' ? null : this.newFeatureForm.durationValue
      })
      this.$message.success('Feature added successfully')

      // Refresh the SKU data
      await this.getList()

      // Update selected SKU with new data
      const updatedSku = this.skus.find(s => s.id === this.selectedSku!.id)
      if (updatedSku) {
        this.selectedSku = updatedSku
      }

      // Reset form
      this.newFeatureForm = {
        featureId: null,
        durationType: 'permanent',
        durationValue: 1
      }
    } catch (error: any) {
      console.error('Failed to add feature:', error)
      this.$message.error(error.response?.data?.status?.message || 'Failed to add feature')
    }
  }

  private async handleRemoveFeature(feature: any) {
    if (!this.selectedSku) return

    this.$confirm(`Remove feature "${feature.featureKey}" from this SKU?`, 'Warning', {
      confirmButtonText: 'Remove',
      cancelButtonText: 'Cancel',
      type: 'warning'
    }).then(async() => {
      try {
        await removeFeatureFromSku(this.selectedSku!.id, feature.id)
        this.$message.success('Feature removed successfully')

        // Refresh the SKU data
        await this.getList()

        // Update selected SKU with new data
        const updatedSku = this.skus.find(s => s.id === this.selectedSku!.id)
        if (updatedSku) {
          this.selectedSku = updatedSku
        }
      } catch (error) {
        console.error('Failed to remove feature:', error)
        this.$message.error('Failed to remove feature')
      }
    })
  }

  private getProductTypeTag(type: string) {
    const map: Record<string, string> = {
      V1: 'info',
      V2: 'success'
    }
    return map[type] || ''
  }

  private formatProductType(type: string) {
    const map: Record<string, string> = {
      V1: 'V1',
      V2: 'V2'
    }
    return map[type] || type
  }
}
